export type FetchInput = string | URL;

export type FetchInit = Record<string, unknown>;

export type FetchImplementation = (input: FetchInput, init?: FetchInit) => Promise<Response>;

export interface TransportSignals {
  timeoutMs?: number | undefined;
  signal?: AbortSignal | undefined;
}

export interface OutboundHeaders {
  headers: Record<string, string>;
  requestId: string;
  idempotencyKey: string | null;
}

export interface BuildHeadersInput {
  requestId: string;
  idempotencyKey?: string | undefined;
  accessToken?: string | undefined;
  accept?: string | undefined;
  contentType?: string | undefined;
  extra?: Record<string, string> | undefined;
  hasBody?: boolean | undefined;
}

export const JSON_CONTENT_TYPE = 'application/json';

export const EXPECTED_JSON_HEADER: Record<string, string> = {
  accept: JSON_CONTENT_TYPE,
  'content-type': JSON_CONTENT_TYPE,
};

export function newRequestId(): string {
  return `req_${globalThis.crypto.randomUUID().replace(/-/g, '').slice(0, 26)}`;
}

export function newIdempotencyKey(scope: string): string {
  return `${scope}_${globalThis.crypto.randomUUID()}`;
}

export function buildHeaders(input: BuildHeadersInput): OutboundHeaders {
  const headers: Record<string, string> = {
    accept: input.accept ?? JSON_CONTENT_TYPE,
    'x-request-id': input.requestId,
    ...(input.extra ?? {}),
  };
  if (input.hasBody === true) {
    headers['content-type'] = input.contentType ?? JSON_CONTENT_TYPE;
  }
  if (input.accessToken !== undefined && input.accessToken !== '') {
    headers.authorization = `Bearer ${input.accessToken}`;
  }
  const idempotencyKey = input.idempotencyKey ?? null;
  if (idempotencyKey !== null) {
    headers['idempotency-key'] = idempotencyKey;
  }
  return { headers, requestId: input.requestId, idempotencyKey };
}

export function combineSignals(
  timeoutMs: number | undefined,
  signal: AbortSignal | undefined,
): {
  signal: AbortSignal;
  stop: () => void;
  cancel: () => void;
  timedOut: () => boolean;
} {
  const controller = new AbortController();
  let timedOut = false;
  const timers: ReturnType<typeof setTimeout>[] = [];

  const abortFrom = (reason?: unknown): void => {
    if (controller.signal.aborted) {
      return;
    }
    const cause = reason instanceof Error ? reason : undefined;
    controller.abort(cause);
  };

  if (signal !== undefined) {
    if (signal.aborted) {
      abortFrom(signal.reason);
    } else {
      signal.addEventListener('abort', () => abortFrom(signal.reason), { once: true });
    }
  }

  if (timeoutMs !== undefined && timeoutMs > 0) {
    timers.push(
      setTimeout(() => {
        timedOut = true;
        abortFrom(new Error('deadline exceeded'));
      }, timeoutMs),
    );
  }

  const stop = (): void => {
    for (const timer of timers) {
      clearTimeout(timer);
    }
  };

  const cancel = (): void => {
    stop();
    abortFrom();
  };

  return { signal: controller.signal, stop, cancel, timedOut: () => timedOut };
}

export function joinUrl(
  baseUrl: string,
  path: string,
  query?: Record<string, string | undefined>,
): string {
  const trimmedBase = baseUrl.replace(/\/+$/, '');
  const normalisedPath = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${trimmedBase}${normalisedPath}`);
  if (query !== undefined) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') {
        url.searchParams.set(key, value);
      }
    }
  }
  return url.toString();
}

export function isSafeBaseUrl(value: string): boolean {
  if (value.startsWith('https://')) {
    return true;
  }
  return value.startsWith('http://localhost') || value.startsWith('http://127.0.0.1');
}
